// Downscale + re-encode an image in the browser before upload. Fixes two things:
//   1. Netlify's serverless functions cap the request body around 6MB. Recipe/
//      cocktail/etc. photos post through the function as multipart with the file
//      LAST, so an oversized phone photo (often 5-12MB) gets the body truncated —
//      the text fields arrive but the image is cut off and silently dropped.
//   2. HEIC iPhone photos would upload but not render in most browsers.
// Re-encoding to a reasonably-sized JPEG solves both. Falls back to the original
// file if the browser can't decode it (e.g. HEIC on a browser without support).
export async function compressImage(file, { maxDim = 1600, quality = 0.82, maxBytes = 4_500_000 } = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // Already small and web-viewable → leave it alone.
  if (file.size <= maxBytes && !/hei[cf]/i.test(file.type)) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // undecodable (e.g. HEIC where unsupported) — send as-is
  }

  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  if (bitmap.close) bitmap.close();

  // Step quality down until it fits comfortably under the body limit.
  let q = quality;
  let blob = null;
  for (let i = 0; i < 5; i++) {
    // eslint-disable-next-line no-await-in-loop
    blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    if (!blob || blob.size <= maxBytes) break;
    q -= 0.15;
  }
  if (!blob) return file;

  const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}
